/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TraceTrackCards.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Grid,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import TraceTrackCardWithTitle from "./TraceTrackCardWithTitle";
import { convertUtcToTimeZone } from "../../../utils/convertUtcToTimeZone";
import { useDispatch, useSelector } from "react-redux";
import { componentStyle } from "../../../config/appTheme";
import ModuleViewMoreDialog from "../../../components/customComponentsV2/dashboard/pages/tickets/ModuleViewMoreDialog";
import { HandleApiActions, labCar } from "../../../redux/actions";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import LinkedTestCaseComponent from "./LinkedTestCaseComponent";
import CustomPaginationV2 from "../../../components/customComponentsV2/common/pagination/CustomPaginationV2";

function TraceTrackCards({ selectedTrackTraceDetails, projectID, variantID }) {
  const profileDetails = useSelector((store) => store.profileReducer);
  const dispatch = useDispatch();
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state?.projectConfigReducer?.selectedVariantDetails,
  );

  // All four ticket types now include the "Linked Components" menu item.
  // Previously VS (ticket_type=6) and VC (ticket_type=3) were excluded.
  // ModuleViewMoreDialog handles all four types (EE, SWM, VS, VC) uniformly.
  const menuItems = [
    {
      id: "ticketDetails",
      label: "Ticket Details",
      component: "ArchitectureTypes",
    },
    {
      id: "legalDetails",
      label: "Legal Details",
      component: "ManufacturerDetails",
    },
    {
      id: "componentDetails",
      label: "Component Details",
      component: "PowerTrainClass",
    },
    {
      id: "parentComponent",
      label: "Parent Component Details",
      component: "PowerTrainClass",
    },
     {
      id: "projectDetails",
      label: "Project Details",
      component: "PowerTrainClass",
    },
    {
      id: "linkedComponent",
      label: "Linked Components",
      component: "PowerTrainClass",
    },
    {
      id: "linkedTestCases",
      label: "Linked Test Cases",
      component: "PowerTrainClass",
    },
    {
      id: "users",
      label: "Users",
      component: "PowerTrainClass",
    },
    {
      id: "attributes",
      label: "Attributes",
      component: "PowerTrainClass",
    },
  ];

  const [selectedMenuItem, setSelectedMenuItem] = useState("ticketDetails");
  const [linkedComponent, setLinkedComponent] = useState([]);
  const [componentSelfData, setComponentSelfData] = useState({});
  const [componentFunction, setComponentFunction] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [slicedAttributes, setSlicedAttributes] = useState([]);
  const handleMenuItemClick = (menuId) => {
    setSelectedMenuItem(menuId);
  };

  const getTicketType = () => {
    switch (Number(selectedTrackTraceDetails?.ticket_type)) {
      case 1:
        return "SWM";
      case 2:
        return "EE";
      case 3:
        return "VC";
      case 6:
        return "VS";
      default:
        return "";
    }
  };

  const getApiAction = () => {
    switch (Number(selectedTrackTraceDetails?.ticket_type)) {
      case 1:
        return {
          apiAction: softwareIntersectionAPI.get_software_intersections,
          apiDataKey: "linked_swm_details",
        };
      case 2:
        return {
          apiAction: labCar.get_ee_intersections,
          apiDataKey: "linked_ee_details",
        };
      default:
        return {};
    }
  };

  const getComponentApiByType = () => {
    console.log("---getComponentApiByType----", getTicketType());
    const vehicleSystemId =
      selectedTrackTraceDetails?.vehicle_system_details?.vs_id ??
      selectedTrackTraceDetails?.vehicle_system_details?.id;
    const vehicleComponentId =
      selectedTrackTraceDetails?.vehicle_component_details?.vc_id ??
      selectedTrackTraceDetails?.vehicle_component_details?.id;

    switch (getTicketType()) {
      case "EE":
        return {
          apiAction: labCar?.get_ee_components,
          componentFunctionKey: "vehicle_system_details",
          searchKeys: ["name"],
          disableApiCall: !selectedTrackTraceDetails?.ee_details?.ee_id,
          headder: {
            name: componentSelfData.assembly_number || "-",
            version: componentSelfData.version || "-",
            description: componentSelfData.description || "-",
          },
          componentKey: "ee_details",
          linkedArrayKey: "linked_ee_ids",
          idKey: "ee_id",
          reqObj: {
            ee_id: selectedTrackTraceDetails?.ee_details?.ee_id,
            ...(selectedTrackTraceDetails?.swm_details?.swm_id && {
              swm_id: selectedTrackTraceDetails?.swm_details?.swm_id,
            }),
          },
        };
      case "SWM":
        return {
          apiAction: labCar?.get_sw_modules,
          componentFunctionKey: "vehicle_functions",
          searchKeys: ["name", "abbreviation"],
          disableApiCall: !selectedTrackTraceDetails?.swm_details?.swm_id,
          headder: {
            name: componentSelfData.name || "-",
            version: componentSelfData.version || "-",
            description: componentSelfData.description || "-",
          },
          componentKey: "swm_details",
          linkedArrayKey: "linked_swm_ids",
          idKey: "swm_id",
          reqObj: {
            ee_id: selectedTrackTraceDetails?.ee_details?.ee_id,
            ...(selectedTrackTraceDetails?.swm_details?.swm_id && {
              swm_id: selectedTrackTraceDetails?.swm_details?.swm_id,
            }),
          },
        };
      case "VC":
        return {
          apiAction: labCar.get_vehicle_components,

          searchKeys: ["description"],
          disableApiCall: !vehicleComponentId,
          headder: {
            name: componentSelfData.name || "-",
            version: componentSelfData.version || "-",
            description: componentSelfData.description || "-",
          },
          componentKey: "vehicle_component_details",
          // linkedArrayKey: "linked_swm_ids",
          idKey: "id",
          reqObj: {
            ...(vehicleSystemId != null && { vs_id: vehicleSystemId }),
            ...(vehicleComponentId != null && { vc_id: vehicleComponentId }),
          },
        };
      case "VS":
        return {
          apiAction: labCar.get_vehicle_systems,
          searchKeys: ["name"],
          disableApiCall: !vehicleSystemId,
          headder: {
            name: componentSelfData.name || "-",
            version: componentSelfData.version || "-",
            description: componentSelfData.description || "-",
          },
          componentKey: "vehicle_system_details",
          idKey: "id",
          reqObj: {
            ...(vehicleSystemId != null && { vs_id: vehicleSystemId }),
          },
        };
      default:
        return {};
    }
  };

  const getComponentDetails = async () => {
    const { componentFunctionKey } = getComponentApiByType();
    try {
      if (!projectID && !variantID && getComponentApiByType().disableApiCall)
        return;

      const resp = await dispatch(
        HandleApiActions({
          ...getComponentApiByType()?.apiAction,
          params: {
            // project_id: selectedProject?.project_id,
            project_id: projectID,
            // variant_id: selectedVariant?.variant_id,
            variant_id: variantID,
            // ee_id: selectedTrackTraceDetails?.ee_details?.ee_id,
            // ...(selectedTrackTraceDetails?.swm_details?.swm_id && {
            //   swm_id: selectedTrackTraceDetails?.swm_details?.swm_id,
            // }),
            ...getComponentApiByType()?.reqObj,
            page_number: page,
            page_size: rowsPerPage,
          },
          show_toast: false,
        }),
      );

      if (resp?.error) {
        setComponentSelfData({});
        setComponentFunction([]);
      } else {
        console.log("resp?.data?.[0]", resp?.data?.[0]);
        setComponentSelfData(resp?.data?.[0]);
        setComponentFunction(resp?.data?.[0]?.[componentFunctionKey]);
      }
    } catch (error) {
      setComponentSelfData({});
      setComponentFunction([]);
    }
  };

  const getLinkedComponentDialogProps = () => {
    switch (getTicketType()) {
      case "VS":
        return {
          apiAction: labCar.get_vehicle_systems,
          apiDataKey: "linked_vs_details",
          type: "VS",
          linkApiProps: {
            vs_id: selectedTrackTraceDetails?.vehicle_system_details?.id,
          },
        };
      case "VC":
        return {
          apiAction: labCar.get_vehicle_components,
          apiDataKey: "linked_vc_details",
          type: "VC",
          linkApiProps: {
            vc_id: selectedTrackTraceDetails?.vehicle_component_details?.id,
            vs_id: selectedTrackTraceDetails?.vehicle_system_details?.id,
          },
        };
      case "EE":
        return {
          apiAction: labCar.get_ee_intersections,
          apiDataKey: "linked_ee_details",
          type: "EE",
          linkApiProps: {
            ee_id: selectedTrackTraceDetails?.ee_details?.ee_id,
          },
        };
      case "SWM":
        return {
          apiAction: softwareIntersectionAPI.get_software_intersections,
          apiDataKey: "linked_swm_details",
          type: "SWM",
          linkApiProps: {
            swm_id: selectedTrackTraceDetails?.swm_details?.swm_id,
            ee_id: selectedTrackTraceDetails?.ee_details?.ee_id,
          },
        };
      default:
        return null;
    }
  };

  const getLinkedComponents = async () => {
    // debugger;
    try {
      if (!projectID && !variantID && getComponentApiByType().disableApiCall) {
        return;
      }

      const apiDetails = getApiAction().apiAction;
      if (!apiDetails) {
        setLinkedComponent([]);
        return;
      }

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: projectID,
            variant_id: variantID,
            ee_id: selectedTrackTraceDetails?.ee_details?.ee_id,
            ...(selectedTrackTraceDetails?.swm_details?.swm_id && {
              swm_id: selectedTrackTraceDetails?.swm_details?.swm_id,
            }),
          },
          show_toast: false,
        }),
      );

      if (resp?.error) {
        setLinkedComponent([]);
      } else {
        console.log(
          "---linked Component---",
          resp?.data?.[0]?.[getApiAction().apiDataKey],
          selectedTrackTraceDetails,
        );

        let allLinkedComponent =
          resp?.data?.[0]?.[getApiAction().apiDataKey] || [];

        let ticketLinkedComponent = allLinkedComponent
          .filter((item) => {
            let typeCastedTicketLinkedIds = selectedTrackTraceDetails?.[
              getComponentApiByType().componentKey
            ]?.[getComponentApiByType().linkedArrayKey]?.map((item) =>
              Number(item),
            );

            return (
              typeCastedTicketLinkedIds.includes(
                Number(item[getComponentApiByType().idKey]),
              ) ?? false
            );
          })
          ?.map((filteredItem) => ({
            ...filteredItem,
            network: filteredItem.network_interface,
          }));
        console.log("---ticketLinkedComponent---", ticketLinkedComponent);
        setLinkedComponent(ticketLinkedComponent);
      }
    } catch (error) {
      setLinkedComponent([]);
    }
  };

  useEffect(() => {
    console.log("---selectedTrackTraceDetails---", selectedTrackTraceDetails);
    getComponentDetails();
    getLinkedComponents();
  }, [selectedTrackTraceDetails]);

  useEffect(() => {
    const attributes = componentSelfData?.attributes || [];
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    setSlicedAttributes(attributes.slice(startIndex, endIndex));
  }, [page, rowsPerPage, componentSelfData?.attributes]);

  return (
    <Grid container>
      <Grid item sm={3}>
        {menuItems.map((item) => (
          <React.Fragment key={item.id}>
            <ListItemButton
              onClick={() => handleMenuItemClick(item.id)}
              sx={{
                ...styled.menuItem,
                backgroundColor:
                  selectedMenuItem === item.id ? "#000000" : "#ffffff",
                color: selectedMenuItem === item.id ? "#ffffff" : "#000000",
                "&:hover": {
                  backgroundColor: "#000000",
                  color: "#ffffff",
                  "& .MuiTypography-root": {
                    color: "#ffffff",
                  },
                },
                borderTop: "1px solid black",
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ ...styled.menuItemContent }}>
                    <Typography
                      sx={{
                        ...styled.menuText,
                        color:
                          selectedMenuItem === item.id ? "#ffffff" : "#000000",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography>
                      <ArrowRightAltIcon />
                    </Typography>
                  </Box>
                }
              />
            </ListItemButton>
          </React.Fragment>
        ))}
      </Grid>
      <Grid item sm={9}>
        <Box
          sx={{
            height: "430px",
            overflow: "auto",
            ...componentStyle.scroll,
          }}
        >
          {selectedMenuItem === "ticketDetails" && (
            <TraceTrackCardWithTitle
              title="Ticket Details"
              data={[
                {
                  key: "Ticket ID",
                  value: selectedTrackTraceDetails?.ticket_id,
                  keyStyle: { fontWeight: 600 },
                },
                {
                  key: "Ticket Status",
                  value: selectedTrackTraceDetails?.status,
                  keyStyle: { fontWeight: 600 },
                },
                {
                  key: "Description",
                  value: selectedTrackTraceDetails?.description,
                  keyStyle: { fontWeight: 600 },
                },
                {
                  key: "Reason",
                  value: selectedTrackTraceDetails?.reason,
                  keyStyle: { fontWeight: 600 },
                },
                {
                  key: "Created At",
                  value: convertUtcToTimeZone(
                    selectedTrackTraceDetails?.created_at,
                    profileDetails?.profile?.profile?.region,
                  ),
                  keyStyle: { fontWeight: 600 },
                },
                {
                  key: "Changed At",
                  value: convertUtcToTimeZone(
                    selectedTrackTraceDetails?.updated_at,
                    profileDetails?.profile?.profile?.region,
                  ),
                  keyStyle: { fontWeight: 600 },
                },
              ]}
            />
          )}

          {selectedMenuItem === "legalDetails" && (
            <Box
              sx={{
                height: "430px",
                overflow: "auto",
                ...componentStyle.scroll,
              }}
            >
              <TraceTrackCardWithTitle
                title="Legal Standard Details"
                data={[
                  {
                    key: "Standard No",
                    value:
                      selectedTrackTraceDetails?.ls_details?.standard_number,
                    keyStyle: { fontWeight: 600 },
                  },
                  {
                    key: "Name",
                    value: selectedTrackTraceDetails?.ls_details?.name,
                    keyStyle: { fontWeight: 600 },
                  },
                  {
                    key: "Revision",
                    value: selectedTrackTraceDetails?.ls_details?.revision,
                    keyStyle: { fontWeight: 600 },
                  },
                  {
                    key: "Description",
                    value: selectedTrackTraceDetails?.ls_details?.description,
                    keyStyle: { fontWeight: 600 },
                    length: 150,
                  },
                ]}
              />
              <TraceTrackCardWithTitle
                title="Legal Requirement Details"
                data={[
                  {
                    key: "Chapter",
                    value: selectedTrackTraceDetails?.lr_details?.chapter,
                    keyStyle: { fontWeight: 600 },
                  },
                  {
                    key: "Requirement No",
                    value: selectedTrackTraceDetails?.lr_details?.req_number,
                    keyStyle: { fontWeight: 600 },
                  },
                  {
                    key: "Description",
                    value: selectedTrackTraceDetails?.lr_details?.description,
                    keyStyle: { fontWeight: 600 },
                    length: 150,
                  },
                ]}
              />
            </Box>
          )}

          {selectedMenuItem === "componentDetails" && (
            <Box
              sx={{
                height: "430px",
                overflow: "auto",
                ...componentStyle.scroll,
              }}
            >
              {selectedTrackTraceDetails?.ee_details?.ee_id &&
                !selectedTrackTraceDetails?.swm_details?.swm_id && (
                  <TraceTrackCardWithTitle
                    title={"EE Component"}
                    data={[
                      {
                        key: "Assembly No",
                        value:
                          selectedTrackTraceDetails?.ee_details
                            ?.assembly_number,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Version",
                        value: selectedTrackTraceDetails?.ee_details?.version,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Description",
                        value:
                          selectedTrackTraceDetails?.ee_details?.description,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Supplier Name",
                        value:
                          selectedTrackTraceDetails?.ee_details?.supplier_name,
                        keyStyle: { fontWeight: 600 },
                      },
                    ]}
                  />
                )}

              {selectedTrackTraceDetails?.swm_details?.swm_id && (
                <TraceTrackCardWithTitle
                  title={"Software Module"}
                  data={[
                    {
                      key: "Software Module",
                      value: selectedTrackTraceDetails?.swm_details?.name,
                      keyStyle: { fontWeight: 600 },
                    },
                    {
                      key: "Version",
                      value: selectedTrackTraceDetails?.swm_details?.version,
                      keyStyle: { fontWeight: 600 },
                    },
                    {
                      key: "Description",
                      value:
                        selectedTrackTraceDetails?.swm_details?.description,
                      keyStyle: { fontWeight: 600 },
                    },
                    {
                      key: "Summary",
                      value: selectedTrackTraceDetails?.swm_details?.summary,
                      keyStyle: { fontWeight: 600 },
                    },
                  ]}
                />
              )}

              {selectedTrackTraceDetails &&
                selectedTrackTraceDetails?.vehicle_system_details?.id && (
                  <TraceTrackCardWithTitle
                    title={"Vehicle System Details"}
                    data={[
                      {
                        key: "Name",
                        value:
                          selectedTrackTraceDetails?.vehicle_system_details
                            ?.name,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Description",
                        value:
                          selectedTrackTraceDetails?.vehicle_system_details
                            ?.description,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Suppliers",
                        value: selectedTrackTraceDetails?.supplier_name || "-",
                        keyStyle: { fontWeight: 600 },
                      },
                    ]}
                  />
                )}

              {selectedTrackTraceDetails?.vehicle_component_details
                ?.component_number && (
                  <TraceTrackCardWithTitle
                    title={"Vehicle Component Details"}
                    data={[
                      {
                        key: "Name",
                        value:
                          selectedTrackTraceDetails?.vehicle_component_details
                            ?.name,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Component No",
                        value:
                          selectedTrackTraceDetails?.vehicle_component_details
                            ?.component_number,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Description",
                        value:
                          selectedTrackTraceDetails?.vehicle_component_details
                            ?.description,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Suppliers",
                        value: selectedTrackTraceDetails?.supplier_name || "-",
                        keyStyle: { fontWeight: 600 },
                      },
                    ]}
                  />
                )}
            </Box>
          )}

          {selectedMenuItem === "parentComponent" && (
            <Box
              sx={{
                height: "430px",
                overflow: "auto",
                ...componentStyle.scroll,
              }}
            >
              <Box sx={{ textAlign: "left", mt: 2 }}>
                <Box>
                  <Typography sx={{ ...componentStyle.font.cardHeadder }}>
                    Project Details
                  </Typography>
                  <Typography sx={{ ...componentStyle.font.text }}>
                    {selectedTrackTraceDetails?.project_details?.name}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ ...componentStyle.font.cardHeadder }}>
                    Variant Details
                  </Typography>
                  <Typography sx={{ ...componentStyle.font.text }}>
                    {selectedTrackTraceDetails?.variant_details[0]?.name}
                  </Typography>
                </Box>
                {selectedTrackTraceDetails?.ee_details?.zone_details
                  ?.zone_name && (
                    <Box>
                      <Typography sx={{ ...componentStyle.font.cardHeadder }}>
                        Zone Name
                      </Typography>
                      <Typography sx={{ ...componentStyle.font.text }}>
                        {
                          selectedTrackTraceDetails?.ee_details?.zone_details
                            ?.zone_name
                        }
                      </Typography>

                      {selectedTrackTraceDetails?.vehicle_component_details
                        ?.id && (
                          <TraceTrackCardWithTitle
                            title={"Vehicle Component"}
                            data={[
                              {
                                key: "Name",
                                value:
                                  selectedTrackTraceDetails
                                    ?.vehicle_component_details?.name,
                                keyStyle: { fontWeight: 600 },
                              },

                              {
                                key: "Description",
                                value:
                                  selectedTrackTraceDetails
                                    ?.vehicle_component_details?.description,
                                keyStyle: { fontWeight: 600 },
                              },
                              // {
                              //   key: "Supplier Name",
                              //   value:
                              //     selectedTrackTraceDetails?.ee_details
                              //       ?.supplier_name,
                              //   keyStyle: { fontWeight: 600 },
                              // },
                            ]}
                          />
                        )}
                    </Box>
                  )}

                {selectedTrackTraceDetails?.swm_details?.swm_id && (
                  <TraceTrackCardWithTitle
                    title={"EE Component"}
                    data={[
                      {
                        key: "Assembly No",
                        value:
                          selectedTrackTraceDetails?.ee_details
                            ?.assembly_number,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Version",
                        value: selectedTrackTraceDetails?.ee_details?.version,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Description",
                        value:
                          selectedTrackTraceDetails?.ee_details?.description,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Supplier Name",
                        value:
                          selectedTrackTraceDetails?.ee_details?.supplier_name,
                        keyStyle: { fontWeight: 600 },
                      },
                    ]}
                  />
                )}
              </Box>
            </Box>
          )}

          {
            selectedMenuItem === "projectDetails" && (
              <Box>
                <TraceTrackCardWithTitle
                    title={"Project Details"}
                    data={[
                      {
                        key: "Name",
                        value:
                          selectedTrackTraceDetails?.project_details
                            ?.name,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Architecture Type",
                        value:
                          selectedTrackTraceDetails?.project_details
                            ?.architecture_name,
                        keyStyle: { fontWeight: 600 },
                      }
                    ]}
                  />
                  <TraceTrackCardWithTitle
                    title={"Variant Details"}
                    data={[
                      {
                        key: "Name",
                        value:
                          selectedTrackTraceDetails?.variant_details[0]
                            ?.name,
                        keyStyle: { fontWeight: 600 },
                      },
                      {
                        key: "Milestone Names",
                        value:
                          selectedTrackTraceDetails?.variant_details[0]
                            ?.milestone_details?.map((milestone) => milestone.milestone_name).join(", "),
                        keyStyle: { fontWeight: 600 },
                      }
                    ]}
                  />
              </Box>
            )
          }

          {selectedMenuItem === "linkedComponent" && (
            <Box
              sx={{
                height: "415px",
                overflow: "auto",
                ...componentStyle.scroll,
              }}
            >
              {getLinkedComponentDialogProps()?.type ? (
                <ModuleViewMoreDialog
                  isEEDialogOpen={true}
                  toggleEEDialog={() => () => { }}
                  selectedComponent={selectedTrackTraceDetails}
                  linkApiProps={getLinkedComponentDialogProps()?.linkApiProps}
                  apiAction={getLinkedComponentDialogProps()?.apiAction}
                  apiDataKey={getLinkedComponentDialogProps()?.apiDataKey}
                  type={getLinkedComponentDialogProps()?.type}
                  inline
                  projectId={projectID}
                  variantId={variantID}
                />
              ) : (
                <Box
                  sx={{
                    p: 1.5,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#666" }}>
                    No linked component data available.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {selectedMenuItem === "linkedTestCases" && (
            <LinkedTestCaseComponent
              linkedTestCases={selectedTrackTraceDetails?.test_case_details}
            />
          )}

          {selectedMenuItem === "users" && (
            <Box
              sx={{
                height: "430px",
                overflow: "auto",
                ...componentStyle.scroll,
              }}
            >
              {selectedTrackTraceDetails?.oem_user_details?.map(
                (user, index) => {
                  return (
                    <TraceTrackCardWithTitle
                      title={"Compliance Requestors"}
                      data={[
                        {
                          key: `${index + 1} Name`,
                          value: user?.name,
                          keyStyle: { fontWeight: 600 },
                          displayInSingleLine: true,
                        },
                        {
                          key: "Email",
                          value: user?.email,
                          keyStyle: { fontWeight: 600, ml: "12px" },
                          displayInSingleLine: true,
                        },
                      ]}
                    />
                  );
                },
              )}
              {selectedTrackTraceDetails?.tech_user_details?.map(
                (user, index) => {
                  return (
                    <TraceTrackCardWithTitle
                      title={"Compliance Reviewers"}
                      data={[
                        {
                          key: `${index + 1} Name`,
                          value: user?.name,
                          keyStyle: { fontWeight: 600 },
                          displayInSingleLine: true,
                        },
                        {
                          key: "Email",
                          value: user?.email,
                          keyStyle: { fontWeight: 600, ml: "12px" },
                          displayInSingleLine: true,
                        },
                      ]}
                    />
                  );
                },
              )}
              {selectedTrackTraceDetails?.parent_supplier_user_details?.map(
                (user, index) => {
                  return (
                    <TraceTrackCardWithTitle
                      title={"Suppliers"}
                      data={[
                        {
                          key: `${index + 1} Name`,
                          value: user?.name,
                          keyStyle: { fontWeight: 600 },
                          displayInSingleLine: true,
                        },
                        {
                          key: `  Email`,
                          value: user?.email,
                          keyStyle: { fontWeight: 600, ml: "12px" },
                          displayInSingleLine: true,
                        },
                      ]}
                    />
                  );
                },
              )}
            </Box>
          )}

          {selectedMenuItem == "attributes" && (
            <Box>
              {Array.isArray(slicedAttributes) &&
                slicedAttributes.length > 0 ? (
                <>
                  <Box
                    sx={{
                      minHeight: "300px",
                      maxHeight: "300px",
                      overflow: "auto",
                    }}
                  >
                    <TraceTrackCardWithTitle
                      title="Attributes"
                      data={slicedAttributes.map((att) => ({
                        key: att.attribute_heading,
                        value: att.attribute_value,
                        valueType: att.attribute_type,
                        keyStyle: { fontWeight: 600 },
                      }))}
                    />
                  </Box>
                  <CustomPaginationV2
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(newPage) => {
                      setPage(newPage);
                    }}
                    totalItems={componentSelfData?.attributes?.length || 0}
                  />
                </>
              ) : (
                <Box
                  sx={{ p: 1.5, border: "1px solid #e0e0e0", borderRadius: 1 }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#666" }}>
                    No attributes added yet.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}

export default TraceTrackCards;

const styled = {
  container: {
    height: "100%",
    overflow: "hidden",
  },
  sidebar: {
    height: "100%",
    width: "250px",
    backgroundColor: "#000000",
    color: "#000000",
    overflow: "auto",
    borderRadius: "0 !important",
    border: "none",
    boxShadow: "none",
    margin: 0,
    padding: 0,
  },
  menuItem: {
    color: "#FFFFFF",
    width: "250px",
    height: "42px",
    "&:hover": {
      backgroundColor: "#8888887c",
    },
  },
  menuItemContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  menuText: {
    fontStyle: "Open Sans",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "20px",
    letterSpacing: "0%",
  },
  contentArea: {
    height: "100%",
    flex: 1,
    overflow: "auto",
    backgroundColor: "#ffffff",
    padding: "0",
    scrollbarGutter: "stable",
  },
  defaultContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
};
